import React, { useState, useEffect } from 'react';
import { marked } from 'marked';
import styles from '../../styles/overlays/GitHubReadme.module.css';
import githubInteraction from '../../utils/GithubInteraction';

interface GitHubReadmeProps {
  githubUrl: string;
  isSmallScreen?: boolean;
  onExpandToggle?: (isExpanded: boolean) => void;
}

interface ReadmeState {
  content: string;
  parsedContent: string;
  imageReferences: Array<{id: string, alt: string, path: string}>;
  loading: boolean;
  error: string | null;
}

const GitHubReadme: React.FC<GitHubReadmeProps> = ({ githubUrl, isSmallScreen = false, onExpandToggle }) => {
  const [readme, setReadme] = useState<ReadmeState>({
    content: '',
    parsedContent: '',
    imageReferences: [],
    loading: true,
    error: null
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch README content using the centralized GitHub utility
  const fetchReadme = async (githubUrl: string) => {
    try {
      setReadme(prev => ({ ...prev, loading: true, error: null }));
      
      const content = await githubInteraction.getReadmeContent(githubUrl);
      
      // Parse the markdown content
      const { html, images } = await parseMarkdown(content);
      
      setReadme({
        content,
        parsedContent: html,
        imageReferences: images,
        loading: false,
        error: null
      });
      
    } catch (error) {
      setReadme({
        content: '',
        parsedContent: '',
        imageReferences: [],
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch README'
      });
    }
  };

  useEffect(() => {
    fetchReadme(githubUrl);
  }, [githubUrl]);

  const handleExpandToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    if (onExpandToggle) {
      onExpandToggle(newExpandedState);
    }
  };


  // Configure marked with security options and extract image references
  const parseMarkdown = async (markdown: string): Promise<{html: string, images: Array<{id: string, alt: string, path: string}>}> => {
    const imageReferences: Array<{id: string, alt: string, path: string}> = [];
    let imageCounter = 0;
    
    // First, protect code blocks and inline code from being modified by image processing
    const codeBlocks: string[] = [];
    const inlineCode: string[] = [];
    
    // Extract and temporarily replace code blocks with placeholders
    markdown = markdown.replace(/```[\s\S]*?```/g, (match) => {
      const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
      codeBlocks.push(match);
      return placeholder;
    });
    
    // Extract and temporarily replace inline code with placeholders
    markdown = markdown.replace(/`([^`]+)`/g, (match) => {
      const placeholder = `__INLINE_CODE_${inlineCode.length}__`;
      inlineCode.push(match);
      return placeholder;
    });
    
    // Now safely process images without interfering with code
    markdown = markdown
      // Handle relative image paths
      .replace(/!\[([^\]]*)\]\(\.\/([^)]+)\)/g, (_, alt, path) => {
        const id = `readme-image-${imageCounter++}`;
        imageReferences.push({ id, alt: alt || 'Image', path });
        return `<div id="${id}" class="readme-image-placeholder"></div>`;
      })
      .replace(/!\[([^\]]*)\]\((?!https?:\/\/)([^)]+)\)/g, (_, alt, path) => {
        const id = `readme-image-${imageCounter++}`;
        imageReferences.push({ id, alt: alt || 'Image', path });
        return `<div id="${id}" class="readme-image-placeholder"></div>`;
      })
      // Handle GitHub's video syntax
      .replace(/\[!\[([^\]]*)\]\(([^)]+)\)\]\(([^)]+)\)/g, '🎥 *Video: $1*')
      .replace(/https:\/\/user-images\.githubusercontent\.com\/[^)]+\.mp4/g, '🎥 *[Video content]*');
    
    // Restore code blocks
    codeBlocks.forEach((code, index) => {
      markdown = markdown.replace(`__CODE_BLOCK_${index}__`, code);
    });
    
    // Restore inline code
    inlineCode.forEach((code, index) => {
      markdown = markdown.replace(`__INLINE_CODE_${index}__`, code);
    });

    const html = marked(markdown, { 
      breaks: true,
      gfm: true
    }) as string;

    return { html, images: imageReferences };
  };

  // Effect to inject ReadmeImage components after HTML is rendered
  useEffect(() => {
    if (readme.imageReferences.length > 0) {
      readme.imageReferences.forEach(({ id, alt, path }) => {
        const placeholder = document.getElementById(id);
        if (placeholder) {
          // Clear the placeholder and replace with our component
          placeholder.innerHTML = '';
          placeholder.className = 'readme-image-container';
          
          // Create a more sophisticated placeholder
          const imageContainer = document.createElement('div');
          imageContainer.style.display = 'flex';
          imageContainer.style.alignItems = 'center';
          imageContainer.style.gap = '0.75rem';
          imageContainer.style.padding = '0.75rem';
          imageContainer.style.backgroundColor = 'var(--color-background-gray, rgba(128, 128, 128, 0.1))';
          imageContainer.style.border = '1px solid var(--color-black, black)';
          imageContainer.style.fontFamily = '"Titillium Web", sans-serif';
          imageContainer.style.fontSize = '0.9rem';
          imageContainer.style.lineHeight = '1.4';
          imageContainer.style.color = 'var(--color-black, black)';
          imageContainer.style.width = '100%';
          imageContainer.style.boxSizing = 'border-box';
          imageContainer.style.margin = '0.5rem 0';
          
          const icon = document.createElement('span');
          icon.textContent = '📷';
          icon.style.fontSize = '1.2rem';
          icon.style.flexShrink = '0';
          
          const details = document.createElement('div');
          details.style.flex = '1';
          details.style.minWidth = '0';
          details.style.wordWrap = 'break-word';
          details.style.overflowWrap = 'break-word';
          
          const altDiv = document.createElement('div');
          altDiv.innerHTML = `<strong>Image:</strong> ${alt}`;
          altDiv.style.marginBottom = '0.25rem';
          
          const pathDiv = document.createElement('div');
          pathDiv.innerHTML = `<strong>Path:</strong> <code style="background-color: var(--color-background-gray, rgba(128, 128, 128, 0.2)); padding: 0.1rem 0.3rem; font-family: 'Courier New', Courier, monospace; font-size: 0.8rem;">${path}</code>`;
          pathDiv.style.marginBottom = '0.25rem';
          pathDiv.style.fontSize = '0.85rem';
          
          details.appendChild(altDiv);
          details.appendChild(pathDiv);
          
          imageContainer.appendChild(icon);
          imageContainer.appendChild(details);
          
          placeholder.appendChild(imageContainer);
        }
      });
    }
  }, [readme.parsedContent, readme.imageReferences]);

  if (readme.loading) {
    return (
      <div className={`${styles.readmeContainer} ${isSmallScreen ? styles.mobile : styles.desktop}`}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading README...</p>
        </div>
      </div>
    );
  }

  if (readme.error) {
    return (
      <div className={`${styles.readmeContainer} ${isSmallScreen ? styles.mobile : styles.desktop}`}>
        <div className={styles.error}>
          <h3>{readme.error}</h3>
          <p>This repository may not have a README file or it may be private.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.readmeContainer} ${isSmallScreen ? styles.mobile : styles.desktop} ${isExpanded ? styles.expanded : ''}`}>
      <div className={styles.readmeHeader}>
        <h3>README.md</h3>
        <button 
          className={styles.expandButton}
          onClick={handleExpandToggle}
          title={isExpanded ? "Collapse README" : "Expand README"}
        >
          {isExpanded ? '⤵' : '⤴'}
        </button>
      </div>
      <div 
        className={styles.readmeContent}
        dangerouslySetInnerHTML={{ 
          __html: readme.parsedContent 
        }}
      />
    </div>
  );
};

export default GitHubReadme;